import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ProShell } from "@/components/ProShell";
import { Logo } from "@/components/Logo";
import { Loader2, CheckCircle2, Clock, XCircle, FileText, ShieldCheck, Edit3 } from "lucide-react";

const FLOW = ["Draft", "Submitted", "Under Review", "Approved"];
const POLICE = { "Not Started": { c: "bg-slate-100 text-slate-600", l: "Not started" }, "In Verification": { c: "bg-amber-50 text-amber-700", l: "In verification (Stage 1)" }, "Verified": { c: "bg-emerald-50 text-emerald-700", l: "Verified (Stage 2)" } };

export function ProApplication({ embedded = false }) {
    const [app, setApp] = useState(null);
    const { user, logout } = useAuth();
    const nav = useNavigate();

    useEffect(() => { api.get("/pro/application").then((r) => setApp(r.data)).catch(() => setApp(false)); }, []);

    const status = app?.verification_status || "Draft";
    const rejected = status === "Rejected";
    const approved = ["Approved", "Verified"].includes(status);
    const stepIndex = rejected ? -1 : FLOW.indexOf(status === "Verified" ? "Approved" : status);

    const Body = (
        <>
            <div className="rounded-b-3xl bg-navy px-5 pb-7 pt-5 text-white">
                <div className="flex items-center justify-between">
                    <Logo size={34} light />
                    <div className="flex items-center gap-2">
                        <span className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold">{app?.pro_id}</span>
                        <button data-testid="app-logout-btn" onClick={async () => { await logout(); nav("/login"); }} className="rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold hover:bg-white/20">Log out</button>
                    </div>
                </div>
                <h1 className="mt-4 font-heading text-2xl font-extrabold">Your application</h1>
                <p className="mt-1 text-sm text-white/70">Hi {user?.name?.split(" ")[0]}, here's your enrolment status.</p>
            </div>

            <div className="px-5 py-5">
                {app === null && <div className="flex justify-center py-12"><Loader2 className="h-7 w-7 animate-spin text-orange" /></div>}
                {app && (
                    <>
                        {rejected ? (
                            <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4" data-testid="app-status-rejected">
                                <XCircle className="mt-0.5 shrink-0 text-red-600" size={20} />
                                <div><p className="font-bold text-red-700">Application not approved</p><p className="mt-0.5 text-sm text-red-600/80">Please review your details and documents, then resubmit.</p></div>
                            </div>
                        ) : approved ? (
                            <div className="flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="app-status-approved">
                                <CheckCircle2 className="mt-0.5 shrink-0 text-emerald-600" size={20} />
                                <div><p className="font-bold text-emerald-700">You're approved!</p><p className="mt-0.5 text-sm text-emerald-700/80">You can now receive jobs from HOMIIE.</p></div>
                            </div>
                        ) : (
                            <ol className="relative ml-2 border-l-2 border-navy/10" data-testid="app-status-timeline">
                                {FLOW.map((s, i) => {
                                    const done = i < stepIndex, current = i === stepIndex;
                                    return (
                                        <li key={s} className="mb-5 ml-5">
                                            <span className={`absolute -left-[11px] flex h-5 w-5 items-center justify-center rounded-full ${done ? "bg-emerald-500" : current ? "bg-orange" : "bg-navy/15"}`}>
                                                {done ? <CheckCircle2 size={13} className="text-white" /> : current ? <Clock size={12} className="text-white" /> : null}
                                            </span>
                                            <p className={`text-sm font-bold ${current ? "text-navy" : done ? "text-navy/70" : "text-navy/40"}`}>{s}</p>
                                            {current && <p className="text-xs text-navy/50">{s === "Draft" ? "Finish and submit your application." : s === "Submitted" ? "Received — awaiting review." : "Our team is reviewing your documents."}</p>}
                                        </li>
                                    );
                                })}
                            </ol>
                        )}

                        <div className="mt-4 flex items-center justify-between rounded-2xl border border-navy/10 bg-white p-4">
                            <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-orange" /><span className="text-sm font-semibold text-navy">Police verification</span></div>
                            <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${(POLICE[app.police_verification] || POLICE["Not Started"]).c}`} data-testid="app-police-stage">{(POLICE[app.police_verification] || POLICE["Not Started"]).l}</span>
                        </div>

                        <div className="mt-4 rounded-2xl border border-navy/10 bg-white p-4">
                            <p className="mb-2 text-sm font-bold text-navy">Submitted documents</p>
                            {(app.documents || []).length === 0 && <p className="text-xs text-navy/50">No documents uploaded yet.</p>}
                            <div className="space-y-2">
                                {(app.documents || []).map((d) => (
                                    <div key={d.type} className="flex items-center gap-2 text-sm text-navy/70"><FileText size={15} className="text-navy/40" /> <span className="capitalize">{d.type.replace("_", " ")}</span> <CheckCircle2 size={14} className="ml-auto text-emerald-500" /></div>
                                ))}
                            </div>
                        </div>

                        {(status === "Draft" || rejected) && (
                            <button data-testid="app-edit-btn" onClick={() => nav("/join-pro")} className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-orange py-3.5 text-sm font-bold text-white shadow-lg shadow-orange/30">
                                <Edit3 size={16} /> {rejected ? "Update & resubmit" : "Complete application"}
                            </button>
                        )}
                    </>
                )}
            </div>
        </>
    );

    if (embedded) return Body;
    return <ProShell>{Body}</ProShell>;
}

export default ProApplication;
